/**
 * Utility for safely parsing JSON fields from database
 * Provides type-safe JSON parsing with fallback defaults
 */

import { type ZodType, z } from 'zod';
import type { JsonValue } from '../db/schema';
import { parseOrDefault } from './validation/schema';
import { serializeToJsonValue } from './validation/transformers/utils';

/**
 * Parse a JSON field with a fallback default value
 * @param value The JSON string, JsonValue, or null/undefined value from database
 * @param defaultValue The fallback value to use if parsing fails
 * @returns Parsed JSON object or the default value
 */
export function parseJsonField<T>(value: string | JsonValue | null | undefined, defaultValue: T): T {
  if (!value) {
    return defaultValue;
  }

  // If it's already an object (JsonValue from Kysely), return it as T
  if (typeof value === 'object' && value !== null) {
    // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
    return value as T;
  }

  // If it's a string, try to parse it
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value) as unknown;
      // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
      return parsed as T;
    } catch {
      return defaultValue;
    }
  }

  return defaultValue;
}

/**
 * Parse a JSON field with Zod validation
 * @param value The JSON string or object to validate
 * @param schema Zod schema for validation
 * @param defaultValue Fallback value if validation fails
 * @returns Validated and parsed object or default value
 */
export function parseJsonFieldWithValidation<T, F extends T | null | undefined>(
  value: string | JsonValue | null | undefined,
  schema: ZodType<T>,
  defaultValue: F
): T | F {
  const parsed = parseJsonField<T | F>(value, defaultValue as T | F);

  if (parsed === defaultValue) {
    return defaultValue;
  }

  return parseOrDefault(schema, parsed, defaultValue, 'json-utils:parse-json-field');
}

/**
 * Serialize values for JSONB storage while preserving types
 * Ensures we never double-encode when persisting to Postgres JSONB columns
 */
export function serializeJsonField<T>(value: T | null | undefined, schema?: ZodType<T>): JsonValue | null {
  if (value === null || value === undefined) {
    return null;
  }

  const dataToSerialize: T = (() => {
    if (!schema) {
      return value as T;
    }
    const validation = schema.safeParse(value);
    if (!validation.success) {
      return value as T;
    }
    return validation.data;
  })();

  try {
    const jsonString = JSON.stringify(dataToSerialize);
    // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
    return JSON.parse(jsonString) as JsonValue;
  } catch {
    return null;
  }
}

/**
 * Safely stringify a value to JSON with optional validation
 * @param value The value to stringify
 * @param schema Optional Zod schema for validation before stringifying
 * @returns JSON string or null if value is null/undefined or validation fails
 */
export function stringifyJsonField<T>(value: T | null | undefined, schema?: ZodType<T>): string | null {
  const serialized = serializeJsonField(value, schema);
  if (serialized === null) {
    return null;
  }
  try {
    return JSON.stringify(serialized);
  } catch {
    return null;
  }
}

/**
 * Normalize unknown input to a JSON-safe string.
 * - Returns the original string unchanged when provided.
 * - Applies serializeToJsonValue to coerce other inputs into JSON-compatible shapes.
 * - Falls back to null when serialization fails.
 */
export function coerceToJsonString(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'string') {
    return value;
  }
  try {
    const normalized = serializeToJsonValue(value);
    if (normalized === null) {
      return null;
    }
    return JSON.stringify(normalized);
  } catch {
    return null;
  }
}

/**
 * Normalize JSON columns that may contain strings or JSONB objects.
 * Returns `undefined` when the value is missing or cannot be parsed.
 */
export function parseJsonColumnValue(value: unknown): unknown {
  if (value === null || value === undefined) {
    return;
  }
  if (typeof value === 'string') {
    try {
      // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
      return JSON.parse(value) as JsonValue;
    } catch {
      return;
    }
  }
  if (typeof value === 'object') {
    return value;
  }
  return;
}

/**
 * Build a Zod preprocessor that normalizes legacy string JSON columns before validation.
 */
export function jsonColumnField<T>(schema: ZodType<T>, fallback: T) {
  return z.preprocess((raw) => {
    const parsed = parseJsonColumnValue(raw);
    return parsed === undefined ? fallback : parsed;
  }, schema);
}

/**
 * Build a nullable variant of `jsonColumnField` that defaults to null.
 */
export function nullableJsonColumnField<T>(schema: ZodType<T | null>) {
  return z.preprocess((raw) => {
    const parsed = parseJsonColumnValue(raw);
    return parsed === undefined ? null : parsed;
  }, schema);
}

const COUNT_QUOTES = /"/g;
const TRAILING_COMMA_PATTERN = /,\s*(\}|\]|$)/u;
const TRAILING_ENDING_COMMA_PATTERN = /,\s*$/u;
const COUNT_OPEN_BRACE = /\{/g;
const COUNT_CLOSE_BRACE = /\}/g;
const COUNT_OPEN_BRACKET = /\[/g;
const COUNT_CLOSE_BRACKET = /\]/g;

const countMatches = (source: string, pattern: RegExp): number => {
  const matches = source.match(pattern);
  return matches ? matches.length : 0;
};

function attemptParse(candidate: string): Record<string, unknown> | null {
  try {
    // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
    const parsed = JSON.parse(candidate) as JsonValue;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    if (Array.isArray(parsed)) {
      return { value: parsed };
    }
    if (parsed !== null && parsed !== undefined) {
      return { value: parsed };
    }
  } catch {
    // ignore
  }
  return null;
}

function balanceStructuralCharacters(input: string): string {
  let normalized = input;

  // Remove dangling comma at the end (",}", ",]", or trailing comma)
  normalized = normalized.replace(TRAILING_COMMA_PATTERN, (_match, closing: string | undefined) => closing ?? '');

  const quoteCount = countMatches(normalized, COUNT_QUOTES);
  if (quoteCount % 2 !== 0) {
    normalized += '"';
  }

  const openBraces = countMatches(normalized, COUNT_OPEN_BRACE);
  const closeBraces = countMatches(normalized, COUNT_CLOSE_BRACE);
  if (openBraces > closeBraces) {
    normalized += '}'.repeat(openBraces - closeBraces);
  }

  const openBrackets = countMatches(normalized, COUNT_OPEN_BRACKET);
  const closeBrackets = countMatches(normalized, COUNT_CLOSE_BRACKET);
  if (openBrackets > closeBrackets) {
    normalized += ']'.repeat(openBrackets - closeBrackets);
  }

  return normalized;
}

function extractKeyValuePairs(input: string): Record<string, unknown> {
  const fallback: Record<string, unknown> = {};
  // eslint-disable-next-line security/detect-unsafe-regex
  const pairRegex = /"([^"\\]+)"\s*:\s*"([^"\\]*)"/g;
  for (const match of input.matchAll(pairRegex)) {
    const [, key, rawValue] = match;
    if (typeof key !== 'string') {
      continue;
    }
    fallback[key] = rawValue;
  }

  // eslint-disable-next-line security/detect-unsafe-regex
  const numberBoolRegex = /"([^"\\]+)"\s*:\s*(true|false|null|-?\d+(?:\.\d+)?)/g;
  for (const match of input.matchAll(numberBoolRegex)) {
    const [, key, rawValue] = match;
    if (typeof key !== 'string') {
      continue;
    }
    if (rawValue === 'true' || rawValue === 'false') {
      fallback[key] = rawValue === 'true';
    } else if (rawValue === 'null') {
      fallback[key] = null;
    } else {
      fallback[key] = Number(rawValue);
    }
  }

  // eslint-disable-next-line security/detect-unsafe-regex
  const arrayRegex = /"([^"\\]+)"\s*:\s*\[([^\]]*)/g;
  for (const match of input.matchAll(arrayRegex)) {
    const [, key, rawArray] = match;
    if (typeof key !== 'string' || typeof rawArray !== 'string') {
      continue;
    }
    const items = [...rawArray.matchAll(/"([^"\\]*)"/g)]
      .map((itemMatch) => itemMatch[1])
      .filter((value): value is string => typeof value === 'string');
    if (items.length > 0) {
      fallback[key] = items;
    }
  }

  return fallback;
}

/**
 * Attempt to parse partial or malformed JSON strings into an object.
 * Falls back to extracting key/value pairs when the payload cannot be fully repaired.
 */
export function bestEffortParseJson<T extends Record<string, unknown> = Record<string, unknown>>(
  input: string | null | undefined
): T {
  if (typeof input !== 'string') {
    // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
    return {} as T;
  }

  const trimmed = input.trim();
  if (!trimmed) {
    // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
    return {} as T;
  }

  const direct = attemptParse(trimmed);
  if (direct) {
    // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
    return direct as T;
  }

  const balanced = balanceStructuralCharacters(trimmed);
  const balancedParse = attemptParse(balanced);
  if (balancedParse) {
    // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
    return balancedParse as T;
  }

  for (let end = balanced.length; end >= 2; end--) {
    const candidate = balanced.slice(0, end).trim().replace(TRAILING_ENDING_COMMA_PATTERN, '');
    if (!candidate) {
      continue;
    }
    const parsed = attemptParse(candidate);
    if (parsed) {
      // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
      return parsed as T;
    }
  }

  const fallback = extractKeyValuePairs(trimmed);
  if (Object.keys(fallback).length > 0) {
    // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
    return fallback as T;
  }

  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  return {} as T;
}
